import { SeatStatuses } from '../enums/SeatStatuses';
import { IAutoSelectSeatsParams } from '../interfaces/IAutoSelectSeatsParams';
import { IAutoSelectSeatsResult } from '../interfaces/IAutoSelectSeatsResult';
import { ISeat } from '../interfaces/ISeat';
import { ISeatRow } from '../interfaces/ISeatRow';
import { IShape } from '../interfaces/IShape';
import { ShapeSeatTypeUtils } from './ShapeSeatTypeUtils';

export class AutoSelectSeatsUtils {

  public static selectSeats(params: IAutoSelectSeatsParams): IAutoSelectSeatsResult {
    const selectedSeatIds: number[] = [];
    let anchorRankToken = params.anchorRankToken;

    for (let pickIndex = 0; pickIndex < params.count; pickIndex++) {
      const picked = this.pickNextSeat(
        params.seats,
        params.shapes,
        params.priceAreaId,
        selectedSeatIds,
        anchorRankToken
      );

      if (!picked) {
        break;
      }

      selectedSeatIds.push(picked.id);
      anchorRankToken = picked.rankToken;
    }

    return {
      selectedSeatIds,
      anchorRankToken,
    };
  }

  private static pickNextSeat(
    seats: readonly ISeat[],
    shapes: readonly IShape[],
    priceAreaId: number,
    selectedSeatIds: readonly number[],
    anchorRankToken: string | null
  ): ISeat | null {
    const selectedSet = new Set(selectedSeatIds);
    const isSelectable = (seat: ISeat): boolean =>
      seat.priceAreaId === priceAreaId
      && this.isSelectableSeat(seat, shapes, selectedSet);
    const zoneSeats = seats.filter(seat => seat.priceAreaId === priceAreaId);
    const rows = this.groupRows(seats);
    const anchorRow = this.resolveAnchorRow(rows, anchorRankToken);

    if (!anchorRow || !this.rowHasSelectableSeat(anchorRow, isSelectable)) {
      return this.pickBestInZone(zoneSeats, isSelectable);
    }

    const orderedRow = this.orderRow(anchorRow.seats);
    const blockIndices = this.blockIndices(orderedRow, selectedSet);

    return this.pickClosestToBlock(orderedRow, blockIndices, isSelectable)
      ?? this.pickBestInZone(orderedRow, isSelectable);
  }

  private static pickBestInZone(
    zoneSeats: readonly ISeat[],
    isSelectable: (seat: ISeat) => boolean
  ): ISeat | null {
    const selectable = zoneSeats.filter(seat => isSelectable(seat));

    if (selectable.length === 0) {
      return null;
    }

    return this.sortByBestSeat(selectable)[0];
  }

  private static pickClosestToBlock(
    orderedRow: readonly ISeat[],
    blockIndices: readonly number[],
    isSelectable: (seat: ISeat) => boolean
  ): ISeat | null {
    if (blockIndices.length === 0) {
      return null;
    }

    const selectable = orderedRow
      .map((seat, index) => ({ seat, index }))
      .filter(({ seat }) => isSelectable(seat));

    if (selectable.length === 0) {
      return null;
    }

    selectable.sort((left, right) => {
      const leftDistance = this.distanceToBlock(left.index, blockIndices);
      const rightDistance = this.distanceToBlock(right.index, blockIndices);

      if (leftDistance !== rightDistance) {
        return leftDistance - rightDistance;
      }

      return this.compareSeatsForBest(left.seat, right.seat);
    });

    return selectable[0].seat;
  }

  private static resolveAnchorRow(
    rows: readonly ISeatRow[],
    anchorRankToken: string | null
  ): ISeatRow | null {
    if (!anchorRankToken) {
      return null;
    }

    return rows.find(row => row.rankToken === anchorRankToken) ?? null;
  }

  private static rowHasSelectableSeat(
    row: ISeatRow,
    isSelectable: (seat: ISeat) => boolean
  ): boolean {
    return row.seats.some(seat => isSelectable(seat));
  }

  private static groupRows(allSeats: readonly ISeat[]): ISeatRow[] {
    const rowMap = new Map<string, ISeatRow>();

    for (const seat of allSeats) {
      if (seat.rankToken === '0') {
        continue;
      }

      const existing = rowMap.get(seat.rankToken);

      if (existing) {
        existing.seats.push(seat);
        continue;
      }

      rowMap.set(seat.rankToken, {
        rankToken: seat.rankToken,
        seats: [seat],
      });
    }

    return [...rowMap.values()];
  }

  private static orderRow(seats: readonly ISeat[]): ISeat[] {
    return [...seats].sort((left, right) => left.element.x - right.element.x);
  }

  private static blockIndices(orderedRow: readonly ISeat[], selectedSet: ReadonlySet<number>): number[] {
    const indices: number[] = [];

    orderedRow.forEach((seat, index) => {
      const inCurrentSelection = selectedSet.has(seat.id);
      const inMyNotedBlock = seat.isMy === true && seat.seatStatusKey === SeatStatuses.NOTED;

      if (inCurrentSelection || inMyNotedBlock) {
        indices.push(index);
      }
    });

    return indices;
  }

  private static isSelectableSeat(
    seat: ISeat,
    shapes: readonly IShape[],
    selectedSet: ReadonlySet<number>
  ): boolean {
    if (selectedSet.has(seat.id)) {
      return false;
    }

    if (seat.seatStatusKey !== SeatStatuses.FREE) {
      return false;
    }

    if (seat.seatWeight <= 0) {
      return false;
    }

    if (seat.loveSeatId) {
      return false;
    }

    const shape = ShapeSeatTypeUtils.resolveSeatShape(seat, shapes);

    return ShapeSeatTypeUtils.isAutoSelectableShape(shape);
  }

  private static sortByBestSeat(seats: readonly ISeat[]): ISeat[] {
    return [...seats].sort((left, right) => this.compareSeatsForBest(left, right));
  }

  private static compareSeatsForBest(left: ISeat, right: ISeat): number {
    if (left.seatWeight !== right.seatWeight) {
      return right.seatWeight - left.seatWeight;
    }

    if (left.element.x !== right.element.x) {
      return left.element.x - right.element.x;
    }

    return left.id - right.id;
  }

  private static distanceToBlock(index: number, blockIndices: readonly number[]): number {
    return Math.min(...blockIndices.map(blockIndex => Math.abs(index - blockIndex)));
  }
}
